import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Archive, History, Plus, PlaySquare, Radio, Replace } from "lucide-react";
import { JOB_MODES, DIALOG_MODES, lockedOnEdit } from "./jobForm";
import { SourceScannerLink } from "./SourceScannerLink";

const MODE_ICON = { live: Radio, historical: History, mass_replace: Replace, archive: Archive, replay: PlaySquare };
const MODE_COLOR = { live: "sky", historical: "teal", mass_replace: "violet", archive: "amber", replay: "emerald" };

const inputCls = "bg-[var(--tg-bg)] border-[var(--tg-border)] focus:border-[var(--c-sky)]";
const selectContentCls = "bg-[var(--tg-elevated)] border-[var(--tg-border)] text-[var(--tg-strong)]";

const AccountItems = ({ accounts }) => (
  <>
    {accounts.length === 0 && <div className="px-3 py-2 text-xs text-[var(--tg-faint)]">Connect an account first</div>}
    {accounts.map((a) => (
      <SelectItem key={a.id} value={a.id}>
        <span className="flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: a.authorized ? "var(--c-emerald)" : "var(--c-rose)" }} />
          {a.label}
        </span>
      </SelectItem>
    ))}
  </>
);

// One card per job type. On a new job it is the first thing the operator picks;
// on an existing job the mode is shown but locked, because the backend cannot
// convert a job between archive/replay/mass-replace and the copy modes.
const ModeSelector = ({ value, onChange, locked }) => (
  <div className="space-y-2">
    <Label className="label-caps">Job Type</Label>
    <div className="grid grid-cols-1 gap-2" data-testid="job-type-selector">
      {JOB_MODES.map((m) => {
        const Icon = MODE_ICON[m.value];
        const c = MODE_COLOR[m.value];
        const active = value === m.value;
        const disabled = locked && !active;
        return (
          <button
            key={m.value}
            type="button"
            disabled={disabled}
            onClick={() => !locked && onChange(m.value)}
            data-testid={`job-type-${m.value}`}
            aria-pressed={active}
            className={`text-left rounded-md border p-3 transition-colors flex items-start gap-3 ${
              active
                ? `border-[var(--c-${c})] bg-[rgb(var(--c-${c}-rgb)/0.10)]`
                : disabled
                  ? "border-[var(--tg-border)] opacity-40 cursor-not-allowed"
                  : "border-[var(--tg-border)] hover:bg-[var(--tg-hover)]"
            }`}
          >
            <Icon className="h-4 w-4 mt-0.5 shrink-0" style={{ color: `var(--c-${c})` }} />
            <span className="min-w-0">
              <span className="block font-semibold text-sm">{m.label}</span>
              <span className="block text-[11px] text-[var(--tg-faint)] mt-0.5">{m.description}</span>
            </span>
          </button>
        );
      })}
    </div>
    {locked && (
      <p className="text-[11px] text-[var(--tg-faint)]">
        A job's type cannot be changed after it is created. Create a new job for a different type.
      </p>
    )}
  </div>
);

const DestinationPicker = ({ form, setForm, dests, templates, toggleDest, perDestinationTemplates = true }) => (
  <div className="space-y-2 rounded-lg border border-[rgb(var(--c-sky-rgb)/0.22)] bg-[rgb(var(--c-sky-rgb)/0.035)] p-3">
    <div className="flex items-center justify-between gap-3">
      <Label className="label-caps">Destinations — select one or more</Label>
      <span className="text-[10px] font-semibold text-[var(--c-sky)]" data-testid="destination-selected-count">
        {form.destinations.length} selected
      </span>
    </div>
    {dests.length > 1 && (
      <div className="flex gap-2">
        <button type="button" onClick={() => setForm((f) => ({ ...f, destinations: dests.map((c) => c.username ? `@${c.username}` : c.resolved_id || c.identifier) }))} className="text-[10px] px-2.5 py-1 rounded border border-[rgb(var(--c-sky-rgb)/0.30)] text-[var(--c-sky)] hover:bg-[rgb(var(--c-sky-rgb)/0.10)]" data-testid="select-all-destinations">Select all</button>
        <button type="button" onClick={() => setForm((f) => ({ ...f, destinations: [] }))} className="text-[10px] px-2.5 py-1 rounded border border-[var(--tg-border)] text-[var(--tg-muted)] hover:bg-[var(--tg-hover)]" data-testid="clear-destinations">Clear</button>
      </div>
    )}
    <div className="space-y-1.5 max-h-48 overflow-y-auto border border-[var(--tg-border)] rounded-md p-2 bg-[var(--tg-bg)]">
      {dests.length === 0 && <p className="text-xs text-[var(--tg-faint)] px-1">Add destination channels first</p>}
      {dests.map((c) => {
        const val = c.username ? `@${c.username}` : c.resolved_id || c.identifier;
        const checked = form.destinations.includes(val);
        const assigned = (form.destination_template_ids || {})[val] || "__none__";
        return (
          <div key={c.id} className={`rounded-md border p-2 space-y-2 ${checked ? "border-[rgb(var(--c-sky-rgb)/0.30)] bg-[rgb(var(--c-sky-rgb)/0.08)]" : "border-transparent hover:bg-[var(--tg-hover)]"}`}>
            <button type="button" onClick={() => toggleDest(val)} data-testid={`dest-option-${c.id}`} className="w-full flex items-center justify-between gap-2 min-w-0 text-sm">
              <span className="truncate min-w-0 flex-1 text-left font-medium">{c.title}</span>
              <span className={`h-4 w-4 rounded border grid place-items-center shrink-0 ${checked ? "bg-[var(--c-sky)] border-[var(--c-sky)]" : "border-[var(--tg-border-hi)]"}`}>{checked && <span className="h-1.5 w-1.5 bg-white rounded-sm" />}</span>
            </button>
            {checked && perDestinationTemplates && (
              <Select value={assigned} onValueChange={(templateId) => setForm((current) => {
                const next = { ...(current.destination_template_ids || {}) };
                if (templateId === "__none__") delete next[val]; else next[val] = templateId;
                return { ...current, destination_template_ids: next };
              })}>
                <SelectTrigger className="h-8 bg-[var(--tg-bg)] border-[var(--tg-border)] text-xs" data-testid={`dest-template-${c.id}`}><SelectValue placeholder="Choose this destination's template" /></SelectTrigger>
                <SelectContent className={selectContentCls}>
                  <SelectItem value="__none__">No destination template</SelectItem>
                  {templates.map((template) => <SelectItem key={template.id} value={template.id}>{template.name} ({template.rules?.length || 0} rules)</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        );
      })}
    </div>
    {perDestinationTemplates && (
      <p className="text-[11px] text-[var(--tg-faint)]">Every destination can use its own replacement template.</p>
    )}
  </div>
);

const CopyStylePicker = ({ form, setForm }) => (
  <div className="space-y-2">
    <Label className="label-caps">Copy Style</Label>
    <div className="grid grid-cols-1 gap-3">
      <button type="button" onClick={() => setForm({ ...form, copy_style: "branded" })} data-testid="style-branded"
        className={`text-left rounded-md border p-3 transition-colors ${form.copy_style === "branded" ? "border-[var(--c-sky)] bg-[rgb(var(--c-sky-rgb)/0.10)]" : "border-[var(--tg-border)] hover:bg-[var(--tg-hover)]"}`}>
        <div className="font-semibold text-sm">Branded Copy</div>
        <div className="text-[11px] text-[var(--tg-faint)] mt-0.5">Text swaps, watermark & image edits applied</div>
      </button>
      <button type="button" onClick={() => setForm({ ...form, copy_style: "clone" })} data-testid="style-clone"
        className={`text-left rounded-md border p-3 transition-colors ${form.copy_style === "clone" ? "border-[var(--c-teal)] bg-[rgb(var(--c-teal-rgb)/0.10)]" : "border-[var(--tg-border)] hover:bg-[var(--tg-hover)]"}`}>
        <div className="font-semibold text-sm">Clone (exact mirror)</div>
        <div className="text-[11px] text-[var(--tg-faint)] mt-0.5">Copies everything as-is, no modifications</div>
      </button>
    </div>
  </div>
);

const DateRange = ({ form, setForm }) => (
  <div className="grid grid-cols-1 gap-3">
    <div className="space-y-2">
      <Label className="label-caps">From Date &amp; Time (optional)</Label>
      <Input type="datetime-local" step="60" data-testid="input-job-date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} className={inputCls} />
    </div>
    <div className="space-y-2">
      <Label className="label-caps">To Date &amp; Time (optional)</Label>
      <Input type="datetime-local" step="60" data-testid="input-job-to-date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} className={inputCls} />
      <p className="text-xs text-[var(--tg-faint)]">Times are in your local timezone. Leave the time at 00:00 to start from the beginning of that day.</p>
    </div>
  </div>
);

// ---- Live / Historical (the pre-existing form, unchanged) --------------------
const CopyJobFields = ({ form, setForm, accounts, sources, dests, templates, toggleDest, editingId }) => (
  <>
    <div className="grid grid-cols-1 gap-3">
      <div className="space-y-2">
        <Label className="label-caps">Source Account</Label>
        <Select value={form.source_account_id} onValueChange={(v) => setForm((f) => ({ ...f, source_account_id: v, dest_account_id: f.dest_account_id || v }))}>
          <SelectTrigger data-testid="select-source-account" className="bg-[var(--tg-bg)] border-[var(--tg-border)]"><SelectValue placeholder="Reads source" /></SelectTrigger>
          <SelectContent className={selectContentCls}><AccountItems accounts={accounts} /></SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label className="label-caps">Destination Account</Label>
        <Select value={form.dest_account_id} onValueChange={(v) => setForm({ ...form, dest_account_id: v })}>
          <SelectTrigger data-testid="select-dest-account" className="bg-[var(--tg-bg)] border-[var(--tg-border)]"><SelectValue placeholder="Posts to dest" /></SelectTrigger>
          <SelectContent className={selectContentCls}><AccountItems accounts={accounts} /></SelectContent>
        </Select>
      </div>
    </div>
    <p className="text-[11px] text-[var(--tg-faint)] -mt-1">
      Use the same account for both, or pick a different account to post to the destination.
    </p>

    <div className="grid grid-cols-1 gap-3">
      <div className="space-y-2">
        <Label className="label-caps">Mode</Label>
        <Select value={form.mode} onValueChange={(v) => setForm((f) => ({ ...f, mode: v, ...(v === "live" ? { from_date: "", to_date: "", min_id: "" } : {}) }))}>
          <SelectTrigger data-testid="select-job-mode" className="bg-[var(--tg-bg)] border-[var(--tg-border)]"><SelectValue /></SelectTrigger>
          <SelectContent className={selectContentCls}>
            <SelectItem value="live">Live (new messages)</SelectItem>
            <SelectItem value="historical">Historical (past)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label className="label-caps">Source — select exactly one</Label>
        <SourceScannerLink source={form.source} account={form.source_account_id || form.account_id} jobId={editingId} />
        <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
          <SelectTrigger data-testid="select-job-source" className="bg-[var(--tg-bg)] border-[var(--tg-border)]"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent className={selectContentCls}>
            {sources.length === 0 && <div className="px-3 py-2 text-xs text-[var(--tg-faint)]">Add a source channel first</div>}
            {sources.map((c) => (
              <SelectItem key={c.id} value={c.username ? `@${c.username}` : c.resolved_id || c.identifier}>{c.title}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>

    <DestinationPicker form={form} setForm={setForm} dests={dests} templates={templates} toggleDest={toggleDest} />
    <CopyStylePicker form={form} setForm={setForm} />

    <div className="space-y-2">
      <Label className="label-caps">Forwarding Channel (optional)</Label>
      <Input data-testid="input-forward-channel" value={form.forward_channel} onChange={(e) => setForm({ ...form, forward_channel: e.target.value })}
        placeholder="@my_buffer_channel or -1001234567890" className={`${inputCls} font-mono`} />
      <p className="text-xs text-[var(--tg-muted)] leading-relaxed">
        Also called the "forwarding account" — a private buffer channel the destination account owns.
        Messages are staged here, then auto-forwarded to the destination so posts show an authentic
        "Forwarded from" label. In Clone mode the original source attribution is preserved (both accounts
        must be members of this channel). Leave empty for direct copy.
      </p>
    </div>

    {form.mode === "historical" && (
      <div className="space-y-3">
        <DateRange form={form} setForm={setForm} />
        <div className="space-y-2">
          <Label className="label-caps">After Msg ID (optional)</Label>
          <Input data-testid="input-job-minid" value={form.min_id} onChange={(e) => setForm({ ...form, min_id: e.target.value })} placeholder="0" className={`${inputCls} font-mono`} />
        </div>
        <p className="text-[11px] text-[var(--tg-faint)]">
          Set only From Date to copy from that day to now. Leave both empty to copy the full history.
        </p>
      </div>
    )}
  </>
);

// ---- Archive (ArchiveJobReq: name, account_id, source, min_id, from_date, to_date) ----
const ArchiveFields = ({ form, setForm, accounts, sources, locked }) => (
  <>
    <div className="rounded-md border border-[rgb(var(--c-amber-rgb)/0.30)] bg-[rgb(var(--c-amber-rgb)/0.06)] p-3 text-[11px] text-[var(--tg-muted)]">
      Captures the source channel's messages and media into the local Content Vault. There is no
      destination and nothing is posted; use a Replay job later to rebuild a channel from the archive.
    </div>
    <div className="space-y-2">
      <Label className="label-caps">Account (member of the source)</Label>
      <Select value={form.account_id} onValueChange={(v) => setForm({ ...form, account_id: v })} disabled={locked.includes("account_id")}>
        <SelectTrigger data-testid="select-archive-account" className="bg-[var(--tg-bg)] border-[var(--tg-border)]"><SelectValue placeholder="Select account" /></SelectTrigger>
        <SelectContent className={selectContentCls}><AccountItems accounts={accounts} /></SelectContent>
      </Select>
      {locked.includes("account_id") && <p className="text-[11px] text-[var(--tg-faint)]">The capturing account is fixed once the job exists.</p>}
    </div>
    <div className="space-y-2">
      <Label className="label-caps">Source Channel</Label>
      {sources.length > 0 && (
        <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
          <SelectTrigger data-testid="select-archive-source" className="bg-[var(--tg-bg)] border-[var(--tg-border)]"><SelectValue placeholder="Select source" /></SelectTrigger>
          <SelectContent className={selectContentCls}>
            {sources.map((c) => (
              <SelectItem key={c.id} value={c.username ? `@${c.username}` : c.resolved_id || c.identifier}>{c.title}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <Input data-testid="input-archive-source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}
        placeholder="@channel or -100123456789" className={`${inputCls} font-mono`} />
    </div>
    <div className="space-y-3">
      <Label className="label-caps">Range (optional — leave empty for the full history)</Label>
      <DateRange form={form} setForm={setForm} />
      <div className="space-y-2">
        <Label className="label-caps">After Msg ID (optional)</Label>
        <Input data-testid="input-archive-minid" value={form.min_id} onChange={(e) => setForm({ ...form, min_id: e.target.value })} placeholder="0" className={`${inputCls} font-mono`} />
      </div>
    </div>
  </>
);

// ---- Replay (ReplayJobReq) ---------------------------------------------------
const ReplayFields = ({ form, setForm, accounts, dests, rules, templates, vaultChannels, toggleDest, toggle, locked }) => {
  const archive = vaultChannels.find((c) => String(c.id) === String(form.vault_channel_id));
  return (
    <>
      <div className="rounded-md border border-[rgb(var(--c-emerald-rgb)/0.30)] bg-[rgb(var(--c-emerald-rgb)/0.06)] p-3 text-[11px] text-[var(--tg-muted)]">
        Rebuilds the destination in chronological order from a vault archive, one message at a time
        with a fixed delay. The original Telegram source is not needed.
      </div>
      <div className="space-y-2">
        <Label className="label-caps">Vault Archive (replay source)</Label>
        <Select value={form.vault_channel_id} onValueChange={(v) => setForm((f) => ({ ...f, vault_channel_id: v, name: f.name || `Replay: ${vaultChannels.find((c) => String(c.id) === v)?.title || "archive"}` }))} disabled={locked.includes("vault_channel_id")}>
          <SelectTrigger data-testid="select-replay-archive" className="bg-[var(--tg-bg)] border-[var(--tg-border)]"><SelectValue placeholder="Select an archive" /></SelectTrigger>
          <SelectContent className={selectContentCls}>
            {vaultChannels.length === 0 && <div className="px-3 py-2 text-xs text-[var(--tg-faint)]">No archives yet — create an Archive job first</div>}
            {vaultChannels.map((c) => (
              <SelectItem key={c.id} value={String(c.id)}>
                {c.title || c.source_ref || c.id} · {c.message_count ?? 0} messages
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {archive && (
          <p className="text-[11px] text-[var(--tg-faint)]" data-testid="replay-archive-summary">
            {archive.message_count ?? 0} messages{archive.media_count ? ` · ${archive.media_count} media` : ""}
            {archive.first_date ? ` · ${String(archive.first_date).slice(0, 10)} → ${String(archive.last_date || "").slice(0, 10)}` : ""}
          </p>
        )}
        {locked.includes("vault_channel_id") && <p className="text-[11px] text-[var(--tg-faint)]">The archive is fixed once the job exists.</p>}
      </div>
      <div className="space-y-2">
        <Label className="label-caps">Posting Account (admin of the destination)</Label>
        <Select value={form.account_id} onValueChange={(v) => setForm({ ...form, account_id: v })} disabled={locked.includes("account_id")}>
          <SelectTrigger data-testid="select-replay-account" className="bg-[var(--tg-bg)] border-[var(--tg-border)]"><SelectValue placeholder="Select account" /></SelectTrigger>
          <SelectContent className={selectContentCls}><AccountItems accounts={accounts} /></SelectContent>
        </Select>
      </div>

      <DestinationPicker form={form} setForm={setForm} dests={dests} templates={templates} toggleDest={toggleDest} perDestinationTemplates={false} />
      <CopyStylePicker form={form} setForm={setForm} />

      {form.copy_style === "branded" && (rules.length > 0 || templates.length > 0) && (
        <div className="space-y-2">
          <Label className="label-caps">Rules & Templates (optional)</Label>
          <div className="space-y-1.5 max-h-36 overflow-y-auto border border-[var(--tg-border)] rounded-md p-2 bg-[var(--tg-bg)]">
            {rules.map((r) => {
              const checked = form.rule_ids.includes(r.id);
              return (
                <button key={r.id} type="button" onClick={() => toggle("rule_ids", r.id)} data-testid={`replay-rule-${r.id}`}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded text-xs font-mono transition-colors ${checked ? "bg-[rgb(var(--c-sky-rgb)/0.10)] text-[var(--c-sky)]" : "hover:bg-[var(--tg-hover)]"}`}>
                  <span className="truncate flex-1 text-left">{r.old_value} → {r.new_value || "(removed)"}</span>
                  <span className={`h-3.5 w-3.5 rounded border grid place-items-center shrink-0 ${checked ? "bg-[var(--c-sky)] border-[var(--c-sky)]" : "border-[var(--tg-border-hi)]"}`} />
                </button>
              );
            })}
            {templates.map((t) => {
              const checked = form.template_ids.includes(t.id);
              return (
                <button key={t.id} type="button" onClick={() => toggle("template_ids", t.id)} data-testid={`replay-template-${t.id}`}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded text-xs transition-colors ${checked ? "bg-[rgb(var(--c-violet-rgb)/0.10)] text-[var(--c-violet)]" : "hover:bg-[var(--tg-hover)]"}`}>
                  <span className="truncate flex-1 text-left">{t.name} <span className="text-[var(--tg-faint)]">({t.rules?.length || 0} rules)</span></span>
                  <span className={`h-3.5 w-3.5 rounded border grid place-items-center shrink-0 ${checked ? "bg-[var(--c-violet)] border-[var(--c-violet)]" : "border-[var(--tg-border-hi)]"}`} />
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="space-y-3">
        <Label className="label-caps">Range (optional — leave empty to replay everything)</Label>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="label-caps">After Msg ID</Label>
            <Input data-testid="input-replay-minid" value={form.min_id} onChange={(e) => setForm({ ...form, min_id: e.target.value })} placeholder="0" className={`${inputCls} font-mono`} />
          </div>
          <div className="space-y-2">
            <Label className="label-caps">Up To Msg ID</Label>
            <Input data-testid="input-replay-maxid" value={form.max_id} onChange={(e) => setForm({ ...form, max_id: e.target.value })} placeholder="latest" className={`${inputCls} font-mono`} />
          </div>
        </div>
        <DateRange form={form} setForm={setForm} />
      </div>

      <div className="grid grid-cols-2 gap-3 items-end">
        <div className="space-y-2">
          <Label className="label-caps">Delay between posts (seconds)</Label>
          <Input type="number" min="0" max="120" step="0.5" data-testid="input-replay-delay" value={form.replay_delay}
            onChange={(e) => setForm({ ...form, replay_delay: e.target.value })} className={`${inputCls} font-mono`} />
          <p className="text-[11px] text-[var(--tg-faint)]">0–120 s. Every message is posted at this fixed interval.</p>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer pb-6" data-testid="toggle-include-deleted">
          <input type="checkbox" checked={form.include_deleted} onChange={(e) => setForm({ ...form, include_deleted: e.target.checked })} className="h-4 w-4" />
          Include messages later deleted at the source
        </label>
      </div>
    </>
  );
};

export const JobFormDialog = ({
  open, onOpenChange, onNewClick, editingId, form, setForm, accounts, sources, dests,
  templates = [], rules = [], vaultChannels = [], onSubmit, onSubmitAndStart, toggleDest, toggle, onPickMassReplace,
}) => {
  const locked = editingId ? lockedOnEdit(form.mode) : [];
  const modeLocked = Boolean(editingId) && locked.includes("mode");
  const supported = DIALOG_MODES.includes(form.mode);
  const title = editingId ? "Edit Job" : "Create Job";
  const description = editingId
    ? "Update this job's configuration. Pause it first; resuming applies these changes without restarting from the beginning."
    : "Pick the job type first; the form below changes to match.";

  const onModeChange = (mode) => {
    if (mode === "mass_replace") {
      onPickMassReplace?.();
      return;
    }
    setForm((f) => ({ ...f, mode }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button onClick={onNewClick} className="bg-[var(--c-sky)] hover:bg-[var(--c-sky-hover)] text-white font-semibold" data-testid="new-job-btn">
          <Plus className="h-4 w-4 mr-2" /> New Job
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--tg-surface)] border-[var(--tg-border)] text-[var(--tg-strong)] w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader className="min-w-0">
          <DialogTitle className="font-head">{title}</DialogTitle>
          <DialogDescription className="text-[var(--tg-faint)]">{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2 min-w-0">
          <ModeSelector value={form.mode} onChange={onModeChange} locked={modeLocked} />

          {!supported ? (
            <p className="text-sm text-[var(--c-rose)]" data-testid="job-type-unsupported">
              This job type ({form.mode}) cannot be edited here.
            </p>
          ) : (
            <>
              <div className="space-y-2">
                <Label className="label-caps">Job Name</Label>
                <Input data-testid="input-job-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={form.mode === "archive" ? "Archive: News Channel" : form.mode === "replay" ? "Replay: News Channel" : "Mirror News Channel"}
                  className={inputCls} />
              </div>

              {(form.mode === "live" || form.mode === "historical") && (
                <CopyJobFields form={form} setForm={setForm} accounts={accounts} sources={sources} dests={dests} templates={templates} toggleDest={toggleDest} editingId={editingId} />
              )}
              {form.mode === "archive" && (
                <ArchiveFields form={form} setForm={setForm} accounts={accounts} sources={sources} locked={locked} />
              )}
              {form.mode === "replay" && (
                <ReplayFields form={form} setForm={setForm} accounts={accounts} dests={dests} rules={rules} templates={templates}
                  vaultChannels={vaultChannels} toggleDest={toggleDest} toggle={toggle} locked={locked} />
              )}

              {editingId || !onSubmitAndStart ? (
                <Button onClick={onSubmit} className="w-full bg-[var(--c-sky)] hover:bg-[var(--c-sky-hover)] text-white font-semibold" data-testid="create-job-btn">
                  {editingId ? "Save Changes" : "Create Job"}
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button onClick={onSubmitAndStart} className="flex-1 bg-[var(--c-sky)] hover:bg-[var(--c-sky-hover)] text-white font-semibold" data-testid="create-job-start-btn">
                    Create & Start
                  </Button>
                  <Button onClick={onSubmit} variant="outline" className="border-[var(--tg-border)] hover:bg-[var(--tg-hover)]" data-testid="create-job-btn">
                    Create only
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
